import AIChatWidget from '@/components/aichat/AiChatWidget';
import PageHero from '@/components/landing/page-hero';
import InputError from '@/components/input-error';
import SeoHead from '@/components/seo/SeoHead';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import LandingLayout from '@/layouts/landing-layout';
import { useForm } from '@inertiajs/react';
import { Eye, MessageSquareQuote } from 'lucide-react';
import { FormEvent } from 'react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

interface TrackResult {
    tracking_number: string;
    current_status: string;
    date_updated: string;
    investigator_message: string | null;
}

export default function GovernanceIntegrityTrack({ trackResult }: { trackResult?: TrackResult | null }) {
    const form = useForm({
        tracking_number: '',
        secret_pin: '',
    });

    const submit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        form.transform((data) => ({
            ...data,
            tracking_number: data.tracking_number.toUpperCase().trim(),
        }));

        form.post('/governance/integrity/track', {
            preserveScroll: true,
        });
    };

    return (
        <LandingLayout>
            <SeoHead
                title="Track Report"
                description="Track a submitted Northeastern College integrity report using its tracking number and secret PIN."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section className="bg-muted pb-16">
                <PageHero
                    title="Track Report"
                    description="Enter your tracking number and secret PIN to view the current public status of your submitted concern."
                    breadcrumbs={[
                        { label: 'Home', href: '/' },
                        { label: 'About' },
                        { label: 'Governance', href: '/governance' },
                        { label: 'Integrity & Whistleblowing System', href: '/governance/integrity' },
                        { label: 'Track Report' },
                    ]}
                    image="/images/hero/nc1.png"
                    imageAlt="Track integrity report"
                    icon={Eye}
                />

                <div className="relative z-20 mx-auto -mt-8 max-w-5xl space-y-8 px-4">
                    <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
                        <Card className="border-border rounded-2xl py-0 shadow-sm">
                            <CardHeader className="p-6">
                                <CardTitle className="text-2xl font-bold">Tracking Credentials</CardTitle>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Use the tracking number and 6-digit secret PIN issued after submission. Public tracking never reveals internal case notes.
                                </p>
                            </CardHeader>
                            <CardContent className="p-6 pt-0">
                                <form onSubmit={submit} className="space-y-5">
                                    <div className="space-y-2">
                                        <Label htmlFor="tracking_number">Tracking Number</Label>
                                        <Input
                                            id="tracking_number"
                                            value={form.data.tracking_number}
                                            onChange={(event) => form.setData('tracking_number', event.target.value)}
                                            placeholder="NC-IWS-2026-000001"
                                        />
                                        <InputError message={form.errors.tracking_number} />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="secret_pin">Secret PIN</Label>
                                        <Input
                                            id="secret_pin"
                                            value={form.data.secret_pin}
                                            onChange={(event) => form.setData('secret_pin', event.target.value)}
                                            inputMode="numeric"
                                            maxLength={6}
                                            placeholder="483291"
                                        />
                                        <InputError message={form.errors.secret_pin} />
                                    </div>

                                    <Button type="submit" disabled={form.processing} className="rounded-full bg-[#00963f] text-white hover:bg-[#008538]">
                                        {form.processing ? 'Checking...' : 'Track Report'}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>

                        <Card className="border-border rounded-2xl py-0 shadow-sm">
                            <CardHeader className="p-6">
                                <CardTitle className="text-2xl font-bold">Public Status</CardTitle>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Only public-facing status details appear here: current status, date updated, and any investigator message.
                                </p>
                            </CardHeader>
                            <CardContent className="p-6 pt-0">
                                {trackResult ? (
                                    <div className="space-y-4">
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <div className="bg-muted/40 border-border rounded-2xl border p-4">
                                                <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">Current Status</div>
                                                <div className="text-foreground mt-2 text-lg font-bold">{trackResult.current_status}</div>
                                            </div>
                                            <div className="bg-muted/40 border-border rounded-2xl border p-4">
                                                <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">Date Updated</div>
                                                <div className="text-foreground mt-2 text-lg font-bold">{trackResult.date_updated}</div>
                                            </div>
                                        </div>

                                        <div className="bg-muted/40 border-border rounded-2xl border p-5">
                                            <div className="mb-3 flex items-center gap-2">
                                                <MessageSquareQuote className="h-5 w-5 text-[#0A7A35]" aria-hidden="true" />
                                                <div className="text-foreground text-sm font-bold">Investigator Message</div>
                                            </div>
                                            <p className="text-muted-foreground text-sm leading-relaxed">
                                                {trackResult.investigator_message ?? 'No investigator message is available at this time.'}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-muted/40 border-border rounded-2xl border p-5">
                                        <p className="text-muted-foreground text-sm leading-relaxed">
                                            No report status is being shown yet. Enter your credentials to retrieve the latest public update.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
